import React, { memo, useContext, useEffect, useState } from 'react';
import { Col, Form, message, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-use';
import { ModalContext } from 'src/contexts/modal-context';
import { editableFields } from '../constants';
import { FormBuilder } from '@components/form';
import { translate } from '@components/i18n';
import { orderTypeSelector } from '@redux/slices/order';
import { TransferItem } from '@models/transfer-item';
import { addTransferItem, deleteTransferItem, fetchTransferItems, fetchTransferItemsByUser, updateTransferItem } from '@redux/slices/transfer-item';
import { transferClientsSelector } from '@redux/slices/transfer-clients';
import { campaignSelector } from '@redux/slices/campaign';
import { moneyTypeSelector } from '@redux/slices/money-type';
import { authSelector } from '@redux/slices/auth';

interface EditModalProps {
    data?: TransferItem.Response;
}

export const EditModal: React.FC<EditModalProps> = memo(({ data }) => {
    const moneyType = useSelector(moneyTypeSelector)
    const { allAvailablePlatforms } = useSelector(orderTypeSelector);
    const { available } = useSelector(transferClientsSelector);
    const { allItems } = useSelector(campaignSelector);
    const authState = useSelector(authSelector);

    const dispatch = useDispatch();
    const [form] = Form.useForm<TransferItem.Response>();
    const [editState, setEditState] = useState<TransferItem.Response>(data);
    const modalContext = useContext(ModalContext);

    const { pathname } = useLocation()
    const isRegister = pathname.includes("/register")

    const onOk = (values): void => {
        if (!isRegister) return;
        const item = allItems
            .filter(item => item.codigoFilial === values.filial)
            .find(item => item.numero + item.divisao === values.numero)

        if (!item) {
            message.error(translate('general.itemRequired'))
            return
        }

        if (item) {
            values.numero = item.numero
            values.descricao = item.descricao
            values.divisao = item.divisao
            values.multiplo = item.multiplo || 1
        }

        values.preco = parseFloat(values.preco)

        if (moneyType.available.length === 1) {
            values.codigoMoeda = moneyType.available[0].codigo
        }

        if (!data) addNew(values);
        else handleUpdate(values);
    };

    const handleUpdate = (values): void => {
        const orderKey = `${new Date().valueOf()}`;
        message.loading({
            content: translate('general.updatingItem'),
            key: orderKey,
            duration: 10,
            className: 'message-styled',
        });

        const req = Object.assign(new TransferItem.RequestUpdate(), { id: data.id, ...values })

        dispatch(
            updateTransferItem({
                data: req,
                notificationKey: orderKey,
                onSuccess: () => {
                    modalContext.handleClose();
                    if (authState.isAdmin) {
                        dispatch(fetchTransferItems());
                    } else {
                        dispatch(fetchTransferItemsByUser())
                    }
                },
            }),
        );
    };

    const addNew = (values): void => {
        const orderKey = `${new Date().valueOf()}`;
        message.loading({
            content: translate('general.registeringItem'),
            key: orderKey,
            duration: 10,
            className: 'message-styled',
        });

        const data = Object.assign(new TransferItem.Request(), values)

        dispatch(
            addTransferItem({
                data,
                notificationKey: orderKey,
                onSuccess: () => {
                    modalContext.handleClose();
                    if (authState.isAdmin) {
                        dispatch(fetchTransferItems());
                    } else {
                        dispatch(fetchTransferItemsByUser())
                    }
                },
            }),
        );
    };

    useEffect(() => {
        if (data) {
            setEditState({ ...data, numero: data.numero + data.divisao });
            form.setFieldsValue({ ...data, numero: data.numero + data.divisao });
        } else {
            form.resetFields();
        }
    }, [data]);

    useEffect(() => {
        if (!form.getFieldValue("codigoMoeda") && moneyType.available.length === 1) {
            form.setFieldsValue({ codigoMoeda: moneyType.available[0].codigo })
            setEditState(prev => ({ ...prev, codigoMoeda: moneyType.available[0].codigo }))
        }
    }, [data])

    const onChangeValues = (
        changedValues: Partial<TransferItem.Response>,
        allValues: TransferItem.Response,
    ): void => {
        const values = {}
        Object.assign(values, allValues);
        Object.assign(values, changedValues);
        setEditState(values as TransferItem.Response);
    };

    const handleDelete = (): void => {
        dispatch(deleteTransferItem(data.id));
        modalContext.handleClose();
    };

    return (
        <Row align="middle">
            <Col span={1} />
            <Col span={22}>
                <FormBuilder
                    onValuesChange={onChangeValues}
                    layout="vertical"
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 24 }}
                    initialValues={{ ...editState }}
                    onFinish={onOk}
                    form={form}
                    fields={editableFields({
                        canEdit: isRegister,
                        newData: !data,
                        optionsAllAvailablePlatforms: allAvailablePlatforms.map((c) => ({
                            id: c.id,
                            name: `${c.id} - ${c.description}`,
                        })),
                        optionsClients: available?.content?.map((c) => ({
                            id: c.id,
                            name: `${c.id} - ${c.razaoSocial}`,
                        })) || [],
                        optionsAllItems: allItems
                            .filter(item => editState?.filial === item.codigoFilial)
                            .map(c => ({
                                id: c.numero + c.divisao,
                                name: `${c.numero} - ${c.divisao} - ${c.descricao}`,
                            })) || [],
                        moneyOptions: moneyType.available.map(money => ({
                            id: money.codigo,
                            name: `${money.codigo} - ${money.descricao}`,
                        }))
                    })}
                    withDelete={!!data && isRegister}
                    onDelete={handleDelete}
                    widthOk={isRegister}
                />
            </Col>
            <Col span={1} />
        </Row>
    );
});
